import moment from 'moment';
import { createUserportalLink, createStaticsLink, userportal as routes } from 'shared/routes';
import { REMOTE_WORK } from 'shared/constants';
// import stripTags from 'shared/lib/stripTags';

const employmentTypeMap = {
  'fulltime-employment': 'FULL_TIME',
  'parttime-employment': 'PART_TIME',
  'fulltime-contract': 'CONTRACTOR',
  'parttime-contract': 'CONTRACTOR',
  'order-onetine': 'CONTRACTOR',
  consultancy: 'CONTRACTOR'
};

// ISO 3166-1 alpha-2 podle slugu zeme; country.id je Google Place ID, ne kod zeme.
// Neznamy slug => addressCountry se vynecha, nikdy se nehada.
const countryCodeMap = {
  'africa-do-sul': 'ZA',
  argentina: 'AR',
  armenia: 'AM',
  australia: 'AU',
  azerbaijan: 'AZ',
  bangladesh: 'BD',
  belgie: 'BE',
  'bosnia-and-herzegovina': 'BA',
  brasil: 'BR',
  bulgaria: 'BG',
  canada: 'CA',
  'ceska-republika': 'CZ',
  china: 'CN',
  'costa-rica': 'CR',
  croatia: 'HR',
  denmark: 'DK',
  egypt: 'EG',
  estonia: 'EE',
  finland: 'FI',
  francie: 'FR',
  georgia: 'GE',
  germany: 'DE',
  greece: 'GR',
  hungary: 'HU',
  iceland: 'IS',
  india: 'IN',
  indonesia: 'ID',
  ireland: 'IE',
  italie: 'IT',
  kazakhstan: 'KZ',
  kyrgyzstan: 'KG',
  latvia: 'LV',
  lithuania: 'LT',
  lucembursko: 'LU',
  mexiko: 'MX',
  moldova: 'MD',
  montenegro: 'ME',
  netherlands: 'NL',
  'new-zealand': 'NZ',
  norway: 'NO',
  pakistan: 'PK',
  peru: 'PE',
  poland: 'PL',
  portugalsko: 'PT',
  rakousko: 'AT',
  romania: 'RO',
  rusko: 'RU',
  serbia: 'RS',
  'severni-makedonie': 'MK',
  slovenia: 'SI',
  slovensko: 'SK',
  spain: 'ES',
  'spojene-staty-americke': 'US',
  sweden: 'SE',
  switzerland: 'CH',
  thailand: 'TH',
  tunisko: 'TN',
  'u-dot-s-dot-virgin-islands': 'VI',
  ukraine: 'UA',
  'united-arab-emirates': 'AE',
  uzbekistan: 'UZ',
  'velka-britanie': 'GB',
  yemen: 'YE'
};

const SALARY_SEGMENT = /[0-9]{3,}|\b(?:kč|czk|eur|usd|huf|pln)\b|[€$]/i;

// Titulek pozice nese podle textace v6 mzdu, emoji, mesto i hook oddeleny svislitkem.
// Do strukturovanych dat patri jen nazev pozice: jinak se hur paruje s dotazem
// uzivatele a pusobi jako spam.
export const cleanJobTitle = (title) => {
  if (!title) return title;

  const [beforePipe] = String(title).split('|');

  // Zahodit koncove useky oddelene carkou, dvojteckou nebo pomlckou, jakmile
  // prvni z nich nese mzdu. Oddelovac je v zachytavajici skupine, takze useky,
  // ktere zustavaji, si drzi puvodni interpunkci ("Product Owner – Business
  // Systems"). Hleda se az od druheho useku: nazev pozice sam o sobe se
  // neodrezava, takze "Microsoft 365 Specialista" projde nedotcene.
  const parts = beforePipe.split(/(\s*[,:]\s*|\s+[–—-]\s+)/);
  let kept = beforePipe;
  for (let i = 2; i < parts.length; i += 2) {
    if (SALARY_SEGMENT.test(parts[i])) {
      kept = parts.slice(0, i - 1).join('');
      break;
    }
  }

  const cleaned = kept
    // Regional_Indicator pokryva vlajky, ktere Extended_Pictographic nechytne.
    .replace(/[\p{Extended_Pictographic}\p{Regional_Indicator}️‍]/gu, '')
    .replace(/\s+/g, ' ')
    .replace(/[\s,:;–—-]+$/, '')
    .trim();

  return cleaned || String(title).trim();
};

const buildAddress = (location) => {
  const countryCode = location.country ? countryCodeMap[location.country.slug] : undefined;

  return {
    '@type': 'PostalAddress',
    // Lide hledaji podle mesta; drive tu byla zeme a pozice v temer vsech
    // mistnich dotazech nefigurovala.
    addressLocality: location.city ? location.city.title : undefined,
    addressCountry: countryCode
  };
};

export default (job) => {
  try {
    const jobUrl = createUserportalLink(routes.job.detail, { slug: job.slug });
    const companyWww = job.company && job.company.www ? job.company.www : undefined;
    const remoteOnly = job.remoteWork === REMOTE_WORK.ONLY;
    const firstCountry = job.locations.length > 0 ? job.locations[0].country : null;

    const payload = {
      '@context': 'https://schema.org',
      '@graph': [{
        '@id': jobUrl,
        '@type': 'JobPosting',
        datePosted: job.publishedAt || job.createdAt,
        description: job.description,
        title: cleanJobTitle(job.title),
        url: jobUrl,
        // Pomaha Googlu pri slucovani duplicit teze pozice z vice zdroju.
        identifier: {
          '@type': 'PropertyValue',
          name: 'Skilleto',
          value: job.id
        },
        // Prihlaska se podava primo na Skilleto, je to filtrovaci kriterium.
        directApply: true,
        // Doba platnosti se neodhaduje: u aktivni pozice ji neznáme (Google
        // doporucuje pole vynechat), u deaktivovane je to den deaktivace.
        validThrough: job.active
          ? undefined
          : (job.deactivatedAt || moment().subtract(1, 'days').toISOString()),
        // Pole je urcene pro plne vzdalene pozice; na prezencnich matlo klasifikaci.
        jobLocationType: remoteOnly ? 'TELECOMMUTE' : undefined,
        applicantLocationRequirements: remoteOnly && firstCountry ? {
          '@type': 'Country',
          name: countryCodeMap[firstCountry.slug] || firstCountry.title
        } : undefined,
        baseSalary: {
          '@type': 'MonetaryAmount',
          currency: 'CZK',
          value: {
            '@type': 'QuantitativeValue',
            minValue: job.monthlyRate.czk,
            maxValue: job.monthlyRateUpTo.czk,
            unitText: 'MONTH'
          }
        },
        employmentType: job.employmentTypes.map(et => employmentTypeMap[et.id]).filter(Boolean),
        hiringOrganization: {
          '@type': 'Organization',
          name: job.company.name,
          // url ma mirit na web firmy, ne na jeji profil na Skilleto.
          url: companyWww,
          sameAs: companyWww,
          logo: job.company.logo ? createStaticsLink(job.company.logo) : undefined
        },
        jobLocation: job.locations.length > 0 ? job.locations.map(location => ({
          '@type': 'Place',
          name: location.city ? location.city.title : location.country.title,
          address: buildAddress(location)
        })) : {
          '@type': 'Place',
          name: 'Česká republika',
          address: {
            '@type': 'PostalAddress',
            addressCountry: 'CZ'
          }
        },
      }]
    };

    return `<script type="application/ld+json">${JSON.stringify(payload)}</script>`;
  } catch {
    return null;
  }
};
